import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useNavigate, useParams } from 'react-router-dom';
import CircularLoading from 'components/CircularLoading';
import { colors } from 'components/Theme/WebTheme';
import { makeStyles } from '@mui/styles';
import {
  MAIN_COLOR,
  SECONDORY_COLOR,
  FONT_FAMILY,
} from '../common/sharedFunctions';
import GoBackButton from 'components/GoBackButton';

const useStyles = makeStyles(() => ({
  textField: {
    '& label.Mui-focused': { color: MAIN_COLOR },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: MAIN_COLOR },
  },
}));

const RATE_FIELDS = [
  { key: 'base_fare', label: 'Tarifa base' },
  { key: 'min_fare', label: 'Tarifa mínima' },
  { key: 'min_fare_distance', label: 'Km en mínima' },
  { key: 'rate_per_unit_distance', label: 'Por distancia' },
  { key: 'rate_per_hour', label: 'Por minuto (T)' },
  { key: 'convenience_fees', label: 'Conveniencia' },
  { key: 'fleet_admin_fee', label: 'Fee fleet' },
];

const REQUIRED_RATE_KEYS = [
  'base_fare',
  'min_fare',
  'rate_per_unit_distance',
  'rate_per_hour',
  'convenience_fees',
];

export default function EditZonificacion() {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    editZonificacionCity,
    fetchZonificacionCities,
    fetchCityCodes,
    generateMasterCode,
    seedCityRatesFromGlobal,
  } = api;
  const settings = useSelector((s) => s.settingsdata.settings);
  const zonificacion = useSelector((s) => s.zonificacion);
  const cartypes = useSelector((s) => s.cartypes);
  const [loading, setLoading] = useState(false);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [codes, setCodes] = useState([]);
  const [data, setData] = useState({
    name: '',
    department: '',
    active: true,
    masterCode: '',
    carTypes: {},
    slotsUsed: 0,
    slotsTotal: 100,
  });

  useEffect(() => {
    dispatch(fetchZonificacionCities());
  }, [dispatch, fetchZonificacionCities]);

  useEffect(() => {
    if (id && zonificacion.cities) {
      const found = zonificacion.cities.find((c) => c.id === id);
      if (found) {
        const carsList = cartypes?.cars || [];
        const carTypes = { ...(found.carTypes || {}) };
        // Rellena convenience_fee_type en tarifas propias antiguas (evita bloqueo al guardar).
        Object.keys(carTypes).forEach((carId) => {
          const ov = carTypes[carId];
          if (!ov || ov.followGlobal !== false) return;
          if (ov.convenience_fee_type) return;
          const globalCar = carsList.find((c) => c.id === carId);
          carTypes[carId] = {
            ...ov,
            convenience_fee_type: globalCar?.convenience_fee_type || 'percentage',
          };
        });
        setData({
          name: found.name || '',
          department: found.department || '',
          active: found.active !== false,
          masterCode: found.masterCode || '',
          carTypes,
          slotsUsed: found.slotsUsed || 0,
          slotsTotal: found.slotsTotal || 100,
          slotsFull: found.slotsFull,
          createdAt: found.createdAt,
        });
        dispatch(fetchCityCodes(id)).then((list) => setCodes(list || []));
      }
    } else if (!id) {
      setData((prev) => ({
        ...prev,
        masterCode: prev.masterCode || generateMasterCode(prev.name || 'CITY'),
      }));
    }
  }, [id, zonificacion.cities, cartypes?.cars, dispatch, fetchCityCodes, generateMasterCode]);

  const cars = useMemo(() => {
    const list = cartypes?.cars || [];
    return [...list].sort((a, b) => {
      const pa = Number(a?.pos);
      const pb = Number(b?.pos);
      const na = Number.isFinite(pa) ? pa : 9999;
      const nb = Number.isFinite(pb) ? pb : 9999;
      if (na !== nb) return na - nb;
      return String(a?.name || '').localeCompare(String(b?.name || ''), 'es');
    });
  }, [cartypes]);

  const buildCityCarRates = (car, followGlobal = true) => ({
    enabled: true,
    name: car.name,
    followGlobal: followGlobal !== false,
    // Solo guarda números si es tarifa propia de ciudad.
    ...(followGlobal === false ? seedCityRatesFromGlobal(car) : {}),
  });

  const toggleCarType = (carId, car) => {
    setData((prev) => {
      const next = { ...prev.carTypes };
      if (next[carId]?.enabled) {
        delete next[carId];
      } else {
        // Por defecto: ciudad usa el Car Type global en vivo (siempre actualizado).
        next[carId] = buildCityCarRates(car, true);
      }
      return { ...prev, carTypes: next };
    });
  };

  const setFollowGlobal = (carId, car, followGlobal) => {
    setData((prev) => {
      const base = {
        enabled: true,
        name: car.name,
        followGlobal: !!followGlobal,
      };
      if (followGlobal) {
        return {
          ...prev,
          carTypes: {
            ...prev.carTypes,
            [carId]: base,
          },
        };
      }
      return {
        ...prev,
        carTypes: {
          ...prev.carTypes,
          [carId]: {
            ...base,
            ...seedCityRatesFromGlobal(car),
          },
        },
      };
    });
  };

  const syncFromGlobal = (carId, car) => {
    setData((prev) => ({
      ...prev,
      carTypes: {
        ...prev.carTypes,
        [carId]: {
          enabled: true,
          name: car.name,
          followGlobal: false,
          ...seedCityRatesFromGlobal(car),
        },
      },
    }));
  };

  const setOverride = (carId, key, value) => {
    setData((prev) => {
      const car = cars.find((c) => c.id === carId);
      const prevOv = prev.carTypes[carId] || {};
      return {
        ...prev,
        carTypes: {
          ...prev.carTypes,
          [carId]: {
            ...prevOv,
            followGlobal: false,
            // Si pasa a tarifa propia, completa tipo de conveniencia (legado sin el campo).
            convenience_fee_type:
              prevOv.convenience_fee_type || car?.convenience_fee_type || 'percentage',
            [key]: value === '' ? null : Number(value),
          },
        },
      };
    });
  };

  const normalizeCarTypesForSave = (carTypes) => {
    const out = {};
    Object.keys(carTypes || {}).forEach((carId) => {
      const ov = carTypes[carId];
      if (!ov?.enabled) return;
      if (ov.followGlobal) {
        out[carId] = {
          enabled: true,
          name: ov.name,
          followGlobal: true,
        };
      } else {
        out[carId] = {
          enabled: true,
          name: ov.name,
          followGlobal: false,
          base_fare: ov.base_fare,
          min_fare: ov.min_fare,
          min_fare_distance: ov.min_fare_distance ?? null,
          rate_per_unit_distance: ov.rate_per_unit_distance,
          rate_per_hour: ov.rate_per_hour,
          convenience_fees: ov.convenience_fees,
          convenience_fee_type: ov.convenience_fee_type || 'percentage',
          fleet_admin_fee: ov.fleet_admin_fee ?? null,
        };
      }
    });
    return out;
  };

  const validateCityRates = () => {
    const entries = Object.entries(data.carTypes || {}).filter(([, v]) => v?.enabled);
    if (!entries.length) {
      return 'Selecciona al menos un tipo de vehículo para la ciudad';
    }
    for (const [carId, ov] of entries) {
      if (ov.followGlobal !== false) continue;
      const car = cars.find((c) => c.id === carId);
      for (const key of REQUIRED_RATE_KEYS) {
        const val = ov[key];
        if (val == null || val === '') {
          const label =
            key === 'rate_per_hour'
              ? 'Por minuto (T)'
              : RATE_FIELDS.find((f) => f.key === key)?.label || key;
          return `Completa "${label}" en ${car?.name || carId} (o deja “Seguir tarifa global”)`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!settings?.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: 'Modo demo: edición crítica deshabilitada' });
      return;
    }
    if (!data.name?.trim()) {
      setCommonAlert({ open: true, msg: 'Nombre de ciudad requerido' });
      return;
    }
    const rateErr = validateCityRates();
    if (rateErr) {
      setCommonAlert({ open: true, msg: rateErr });
      return;
    }
    const payloadBase = {
      ...data,
      name: data.name.trim(),
      masterCode: String(data.masterCode || generateMasterCode(data.name)).toUpperCase(),
      carTypes: normalizeCarTypesForSave(data.carTypes),
    };
    setLoading(true);
    try {
      if (id) {
        await dispatch(editZonificacionCity({ id, ...payloadBase }, 'Update'));
      } else {
        await dispatch(editZonificacionCity(payloadBase, 'Add'));
      }
      navigate('/zonificacion');
    } catch (e) {
      setCommonAlert({ open: true, msg: e?.message || 'Error al guardar' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenCodes = async () => {
    if (!id) {
      setCommonAlert({ open: true, msg: 'Guarda la ciudad primero' });
      return;
    }
    if (!settings?.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: 'Modo demo' });
      return;
    }
    setLoading(true);
    try {
      const fresh = await dispatch(editZonificacionCity({ id }, 'GenerateCodes'));
      const list = await dispatch(fetchCityCodes(id));
      setCodes(list || []);
      setCommonAlert({
        open: true,
        msg: `Códigos regenerados: ${Array.isArray(fresh) ? fresh.length : 0} individuales disponibles`,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportCodesCsv = () => {
    const rows = [['code', 'type', 'used', 'active']];
    codes.forEach((c) => {
      rows.push([c.code, c.type, c.used ? '1' : '0', c.active ? '1' : '0']);
    });
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `codigos_${data.name || id || 'ciudad'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <CircularLoading />;

  return (
    <div>
      <GoBackButton onClick={() => navigate('/zonificacion')} />
      <Card style={{ padding: 20, marginTop: 10, borderColor: SECONDORY_COLOR }}>
        <Typography variant="h5" style={{ fontFamily: FONT_FAMILY, marginBottom: 16 }}>
          {id ? 'Editar ciudad' : 'Nueva ciudad'} — Colombia
        </Typography>

        {commonAlert.open ? (
          <Alert severity="info" onClose={() => setCommonAlert({ open: false, msg: '' })} sx={{ mb: 2 }}>
            {commonAlert.msg}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              className={classes.textField}
              label="Ciudad"
              value={data.name}
              onChange={(e) => {
                const name = e.target.value;
                setData((p) => ({
                  ...p,
                  name,
                  masterCode: id ? p.masterCode : generateMasterCode(name || 'CITY'),
                }));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              className={classes.textField}
              label="Departamento"
              value={data.department}
              onChange={(e) => setData((p) => ({ ...p, department: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              className={classes.textField}
              label="Código maestro"
              value={data.masterCode}
              onChange={(e) =>
                setData((p) => ({ ...p, masterCode: e.target.value.toUpperCase() }))
              }
              helperText="Reutilizable hasta agotar los 100 cupos"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!data.active}
                  onChange={(e) => setData((p) => ({ ...p, active: e.target.checked }))}
                />
              }
              label="Ciudad activa"
            />
            <Typography variant="body2" color="textSecondary">
              Cupos: {data.slotsUsed || 0} / {data.slotsTotal || 100}
              {(data.slotsUsed || 0) >= (data.slotsTotal || 100)
                ? ' — Fase abierta (código normal)'
                : ' — Fase cupo especial'}
            </Typography>
          </Grid>
        </Grid>

        <Box mt={3}>
          <Typography variant="h6" style={{ fontFamily: FONT_FAMILY }}>
            Tipos de vehículo y tarifas
          </Typography>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            <strong>Referencia:</strong> el Car Type global (menú Car Types). Por defecto la ciudad
            usa esa referencia y se actualiza sola. Solo desactiva el switch si esta ciudad necesita
            precios distintos; verás la referencia al lado para confirmar o ajustar.
          </Alert>
          <FormGroup>
            {cars.map((car) => {
              const enabled = !!data.carTypes?.[car.id]?.enabled;
              const ov = data.carTypes?.[car.id] || {};
              const followGlobal =
                ov.followGlobal === true ||
                (ov.followGlobal !== false &&
                  !RATE_FIELDS.some((f) => ov[f.key] != null && ov[f.key] !== '') &&
                  !ov.convenience_fee_type);
              const refConveniencia =
                car.convenience_fee_type === 'flat'
                  ? `fijo ${car.convenience_fees ?? '-'}`
                  : `${car.convenience_fees ?? '-'}%`;
              return (
                <Box key={car.id} sx={{ border: '1px solid #eee', borderRadius: 1, p: 1.5, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={enabled} onChange={() => toggleCarType(car.id, car)} />
                    }
                    label={car.name}
                  />
                  {enabled ? (
                    <Box sx={{ pl: 2, pt: 1 }}>
                      <Box
                        sx={{
                          bgcolor: '#f7f7f7',
                          borderRadius: 1,
                          p: 1.5,
                          mb: 1.5,
                          border: '1px dashed #ccc',
                        }}
                      >
                        <Typography variant="subtitle2" style={{ fontFamily: FONT_FAMILY }}>
                          Referencia — Car Type global (vigente)
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Base {car.base_fare ?? '-'} · Mín {car.min_fare ?? '-'}
                          {car.min_fare_distance != null && car.min_fare_distance !== ''
                            ? ` (${car.min_fare_distance} km)`
                            : ''}{' '}
                          · Distancia {car.rate_per_unit_distance ?? '-'} · Min {car.rate_per_hour ?? '-'} ·
                          Conveniencia {refConveniencia} · Fleet {car.fleet_admin_fee ?? '-'}
                        </Typography>
                      </Box>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={followGlobal}
                            onChange={(e) => setFollowGlobal(car.id, car, e.target.checked)}
                          />
                        }
                        label="Usar referencia global (siempre actualizada)"
                      />

                      {followGlobal ? (
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                          La app cobrará exactamente la referencia global vigente de {car.name}.
                        </Typography>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" style={{ fontFamily: FONT_FAMILY }}>
                              Tarifa de esta ciudad (ajustada)
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => syncFromGlobal(car.id, car)}>
                              Partir de la referencia actual
                            </Button>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={4} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Tipo conveniencia</InputLabel>
                                <Select
                                  label="Tipo conveniencia"
                                  value={ov.convenience_fee_type || car.convenience_fee_type || 'percentage'}
                                  onChange={(e) =>
                                    setData((prev) => ({
                                      ...prev,
                                      carTypes: {
                                        ...prev.carTypes,
                                        [car.id]: {
                                          ...prev.carTypes[car.id],
                                          followGlobal: false,
                                          convenience_fee_type: e.target.value,
                                        },
                                      },
                                    }))
                                  }
                                >
                                  <MenuItem value="percentage">Porcentaje</MenuItem>
                                  <MenuItem value="flat">Fijo</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {RATE_FIELDS.map((f) => (
                              <Grid item xs={6} sm={4} md={2} key={f.key}>
                                <TextField
                                  size="small"
                                  type="number"
                                  required
                                  label={f.label}
                                  value={ov[f.key] ?? ''}
                                  onChange={(e) => setOverride(car.id, f.key, e.target.value)}
                                  fullWidth
                                  helperText={
                                    car[f.key] != null ? `Ref. global: ${car[f.key]}` : 'Sin ref. global'
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </FormGroup>
        </Box>

        <Box mt={3} display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            style={{ backgroundColor: MAIN_COLOR, color: colors.WHITE }}
            onClick={handleSubmit}
          >
            Guardar
          </Button>
          {id ? (
            <>
              <Button variant="outlined" onClick={handleRegenCodes}>
                Regenerar códigos libres
              </Button>
              <Button variant="outlined" onClick={exportCodesCsv} disabled={!codes.length}>
                Exportar códigos CSV ({codes.length})
              </Button>
            </>
          ) : null}
        </Box>

        {id && codes.length ? (
          <Box mt={2} sx={{ maxHeight: 240, overflow: 'auto' }}>
            <Typography variant="subtitle2">
              Códigos (muestra): {codes.filter((c) => c.type === 'individual' && !c.used).length} individuales libres
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              {codes
                .slice(0, 30)
                .map((c) => `${c.code} [${c.type}${c.used ? ',usado' : ''}]`)
                .join(' · ')}
              {codes.length > 30 ? ' …' : ''}
            </Typography>
          </Box>
        ) : null}
      </Card>
    </div>
  );
}
